/*
 * LienzoMoments — candado del "Agregar al carrito"
 * ---------------------------------------------------------------------------
 * Un pedido sin foto no es un pedido: es una persecucion por WhatsApp que a
 * veces acaba en reembolso y en una resena mala. Este archivo impide que se
 * cree.
 *
 * Deroga la parte de DT-01 que dejaba comprar sin foto (ver DT-10 en
 * docs/decisions.md). El flujo post-compra sigue existiendo como red para el
 * pedido que llegue por otra via, pero la pagina ya no lo provoca.
 *
 * QUE HACE
 * Evalua el estado de la compra en cada cambio y lo aplica a los dos CTA de
 * la pagina: el del formulario de producto y el de la barra pegada.
 *
 *   'no-size'         no hay tamano elegido
 *   'no-package'      hay tamano pero ningun paquete seleccionable
 *   'missing-photos'  faltan fotos para la variante elegida
 *   'needs-decision'  estan todas, pero alguna no coincide con el formato y
 *                     el cliente no ha elegido que hacer con ella (DT-11)
 *   'ready'           todo puesto
 *
 * DE DONDE SALE EL ESTADO
 * Del DOM, no de un espejo en memoria. Los eventos (`lm:variant`,
 * `lm:photos-changed`) solo disparan la re-evaluacion; los datos se releen
 * cada vez del radio marcado y de las ranuras de foto. Guardar una copia
 * obligaria a mantenerla sincronizada con tres scripts que ya escriben en el
 * mismo DOM, y una copia desincronizada aqui vuelve a abrir el agujero.
 *
 * QUE CUENTA COMO FOTO SUBIDA
 * Una ranura con `data-lm-filled="true"`, que escribe
 * `lienzomoments-upload.js` SOLO cuando el archivo paso su validacion. Asi un
 * HEIC rechazado no habilita el boton. Tampoco cuenta la que lleva
 * `data-lm-rejected="true"`: una foto que no entra en ningun formato.
 *
 * Si ese script no arranco (`data-lm-ready` ausente en el contenedor), se cae
 * a mirar `input.files`. Es menos estricto, pero la alternativa —un CTA
 * deshabilitado para siempre porque un asset dio 404— es una tienda cerrada.
 *
 * LOS TEXTOS VIENEN DE LIQUID
 * El bloque de subida escribe un `<script type="application/json">` con los
 * mensajes. Aqui no hay ni una cadena en espanol: el dia que la tienda hable
 * otro idioma se traduce el schema y este archivo no se toca. Si el JSON
 * faltara, el candado sigue cerrando; lo unico que se pierde es el cambio de
 * texto del boton.
 */
(() => {
  'use strict';

  /* La barra pegada tambien pide este archivo. El navegador lo descarga una
     vez pero lo ejecuta una vez por etiqueta. */
  if (window.__lmGateReady) return;
  window.__lmGateReady = true;

  const PLACEHOLDER = /\{count\}/g;

  /* --- Mensajes ---------------------------------------------------------- */

  let messages = {};

  const readMessages = () => {
    const node = document.querySelector('[data-lm-gate-messages]');
    if (!node) return;
    try {
      messages = JSON.parse(node.textContent) || {};
    } catch {
      messages = {};
    }
  };

  /** Devuelve null cuando no hay mensaje: el boton conserva el suyo. */
  const messageFor = (state, missing, required) => {
    const fill = (key, count) => (messages[key] || '').replace(PLACEHOLDER, String(count)) || null;

    switch (state) {
      case 'no-size':
        return messages.noSize || null;
      case 'no-package':
        return messages.noPackage || null;
      case 'needs-decision':
        return (missing === 1 ? messages.decisionOne : messages.decisionMany) || messages.decisionOne || null;
      case 'ready':
        return messages.ready || null;
      case 'missing-photos':
        // Nada subido todavia: se pide el total. Ya hay algo: se pide el resto.
        if (missing === required) return fill(required === 1 ? 'photosOne' : 'photosMany', required);
        return fill(missing === 1 ? 'missingOne' : 'missingMany', missing);
      default:
        return null;
    }
  };

  /* --- Lectura del estado ------------------------------------------------ */

  /**
   * El nodo de texto del CTA. No se guarda en una variable de modulo porque
   * Horizon puede reemplazar la seccion entera desde el editor y un nodo
   * viejo escribiria en un boton que ya no esta en la pagina. Se cachea en el
   * propio boton, que muere con el, para no recorrer los hijos en cada render.
   */
  const labelNode = (button) => {
    const cached = button.__lmLabelNode;
    if (cached && cached.parentNode === button) return cached;

    const found = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ''
    );
    if (!found) return null;

    button.__lmLabelNode = found;
    if (button.dataset.lmLabelDefault == null) {
      button.dataset.lmLabelDefault = found.textContent.trim();
    }
    return found;
  };

  const setLabel = (button, text) => {
    const node = labelNode(button);
    if (!node) return;
    const next = text || button.dataset.lmLabelDefault || node.textContent;
    if (node.textContent !== next) node.textContent = next;
  };

  const uploadRoot = () => document.querySelector('[data-lm-upload]');

  /**
   * Cuantas de las `required` primeras ranuras traen una foto validada.
   * Se limita a las primeras porque una variante mas chica conserva las fotos
   * de la anterior en las ranuras ocultas, y esas no cuentan para este pedido.
   */
  const uploadedPhotos = (required) => {
    const root = uploadRoot();
    if (!root) return 0;

    const validated = root.dataset.lmReady === 'true';
    const slots = Array.from(root.querySelectorAll('[data-lm-slot]')).slice(0, required);

    return slots.filter((slot) => {
      // Una foto que no entra en ningun formato (DT-11) no sirve para el
      // pedido: cuenta como no subida y el boton pide otra.
      if (validated) return slot.dataset.lmFilled === 'true' && slot.dataset.lmRejected !== 'true';
      const input = slot.querySelector('[data-lm-file]');
      return Boolean(input && input.files && input.files.length > 0);
    }).length;
  };

  /**
   * Estado completo, releido del DOM. Devuelve null cuando la pagina no lleva
   * selector: sin el no hay nada que vigilar y el CTA se queda como estaba.
   */
  const evaluate = () => {
    if (!document.querySelector('[data-lm-size-input]')) return null;

    if (!document.querySelector('[data-lm-size-input]:checked')) {
      return { state: 'no-size', required: 0, uploaded: 0, missing: 0 };
    }

    // `:not([disabled])` deja fuera los grupos de tamano ocultos y las
    // variantes agotadas, que es exactamente lo que no se puede comprar.
    const pkg = document.querySelector('[data-lm-tier] .lm-tier__input:checked:not([disabled])');
    if (!pkg) return { state: 'no-package', required: 0, uploaded: 0, missing: 0 };

    /*
     * Sin bloque de subida en la pagina no hay nada que exigir: pedir una
     * foto que no se puede dar dejaria el CTA cerrado para siempre. Pasa si
     * alguien quita el bloque desde el editor, y una tienda que no vende es
     * peor problema que el que arregla el candado.
     */
    if (!uploadRoot()) return { state: 'ready', required: 0, uploaded: 0, missing: 0 };

    const required = Number(pkg.dataset.lmPhotos) || 1;
    const uploaded = uploadedPhotos(required);
    const missing = Math.max(0, required - uploaded);
    if (missing > 0) return { state: 'missing-photos', required, uploaded, missing };

    /*
     * Fotos que esperan decision (DT-11). Se cuentan despues de las que
     * faltan: primero se pide subir, luego decidir. `missing` lleva aqui el
     * numero de decisiones pendientes, para elegir el texto en singular o
     * plural.
     */
    const pending = Array.from(uploadRoot().querySelectorAll('[data-lm-slot]'))
      .slice(0, required)
      .filter((slot) => slot.dataset.lmPending === 'true').length;
    if (pending > 0) return { state: 'needs-decision', required, uploaded, missing: pending };

    return { state: 'ready', required, uploaded, missing: 0 };
  };

  /* --- Aplicacion -------------------------------------------------------- */

  const mainButton = () => document.querySelector('product-form-component form button[name="add"]');
  const stickyButton = () => document.querySelector('[data-lm-sticky-cta]');
  const statusNode = () => document.querySelector('[data-lm-gate-status]');

  /**
   * A donde manda la barra pegada segun lo que falte. En 'ready' no manda a
   * ningun sitio: pulsa el CTA real, que es lo que promete su texto.
   */
  const STICKY_TARGET = {
    'no-size': '#lm-selector',
    'no-package': '#lm-selector',
    'missing-photos': '#lm-upload',
    'needs-decision': '#lm-upload',
  };

  let lastMessage = null;

  const render = () => {
    const result = evaluate();
    if (!result) return;

    const { state, required, missing } = result;
    const ready = state === 'ready';
    const message = messageFor(state, missing, required);

    /* --- CTA principal --- */
    const main = mainButton();
    if (main) {
      if (ready) {
        // Solo se rehabilita lo que cerro este archivo. Si Horizon lo
        // deshabilito por su cuenta (agotado, envio en curso), manda el.
        if (main.dataset.lmGated === 'true') {
          delete main.dataset.lmGated;
          main.disabled = false;
        }
        main.removeAttribute('aria-disabled');
      } else {
        main.dataset.lmGated = 'true';
        main.disabled = true;
        main.setAttribute('aria-disabled', 'true');
      }
      setLabel(main, message);
    }

    /* --- CTA de la barra pegada ---
       Espeja el texto, no el bloqueo: la barra no vende, encamina. Un boton
       apagado que hay que pulsar para llegar justo a lo que falta seria una
       trampa. Ver DT-10. */
    const sticky = stickyButton();
    if (sticky) {
      sticky.dataset.lmGateState = state;
      setLabel(sticky, message);

      const target = STICKY_TARGET[state];
      if (target) {
        // `lienzomoments-scroll.js` resuelve el clic por delegacion.
        sticky.dataset.lmGoto = target;
      } else {
        delete sticky.dataset.lmGoto;
      }
    }

    /* --- Aviso para lectores de pantalla y para quien mira ---
       Se escribe solo cuando cambia. Un aria-live que recibe el mismo texto
       otra vez no anuncia nada, pero uno que recibe escrituras en cada evento
       acaba interrumpiendose a si mismo. */
    const status = statusNode();
    if (status) {
      // El aviso repite el mensaje del boton dentro de la frase del ajuste,
      // que es la que aporta el motivo. Sin plantilla, no hay aviso: el
      // mensaje suelto ya lo dice el boton dos veces mas arriba.
      const template = messages.statusTemplate || '';
      const text = ready || !message || !template ? '' : template.replace(/\{message\}/g, message);
      if (text !== lastMessage) {
        status.textContent = text;
        lastMessage = text;
      }
      status.hidden = text === '';
    }
  };

  /* --- El CTA de la barra pegada cuando ya no falta nada ------------------ */

  document.addEventListener('click', (event) => {
    const sticky = event.target.closest && event.target.closest('[data-lm-sticky-cta]');
    if (!sticky || sticky.dataset.lmGateState !== 'ready') return;

    const main = mainButton();
    if (!main || main.disabled) return;

    event.preventDefault();
    main.click();
  });

  /* --- Disparadores ------------------------------------------------------ */

  /*
   * Los eventos propios son la via normal. Los `change` del DOM son la red:
   * si `lienzomoments-selector.js` o `lienzomoments-upload.js` fallaran, el
   * candado sigue reaccionando a lo que el usuario toca. Releer el DOM en
   * cada disparo hace que sobrar disparadores no cueste correccion, solo unos
   * microsegundos.
   */
  ['lm:variant', 'lm:photos-changed'].forEach((name) => document.addEventListener(name, render));

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!target || !target.matches) return;
    if (target.matches('[data-lm-format-input], [data-lm-size-input], .lm-tier__input, [data-lm-file]')) render();
  });

  const boot = () => {
    readMessages();
    lastMessage = null;
    render();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
